import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { tap } from 'rxjs/operators';
import { ImageCacheService } from '../../services/cache/image-cache.service';
import { GuardianActions } from '../guardian/guardian.actions';
import { StudentActions } from '../students/student.actions'; // Assuming you have learner actions
import { ImageCacheActions } from './image-cache.actions';

@Injectable()
export class ImageCacheEffects {
  private readonly actions$ = inject(Actions);
  private readonly imageCacheService = inject(ImageCacheService);

  // Clear cache when image is explicitly updated
  clearImageCache$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ImageCacheActions.imageUpdated, ImageCacheActions.clearImageCache),
        tap(({ imageUrl }) => {
          this.imageCacheService.clearAndRefresh(imageUrl);
        }),
      ),
    { dispatch: false },
  );

  // Clear all cache
  clearAllImageCache$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ImageCacheActions.clearAllImageCache),
        tap(() => {
          this.imageCacheService.clearAll();
        }),
      ),
    { dispatch: false },
  );

  // Handle profile image updates
  profileImageUpdated$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ImageCacheActions.profileImageUpdated),
        tap(({ oldImageUrl, newImageUrl }) => {
          if (oldImageUrl) {
            this.imageCacheService.clearAndRefresh(oldImageUrl);
          }
          // Preload the new image
          if (newImageUrl) {
            this.imageCacheService.preloadImage(newImageUrl);
          }
        }),
      ),
    { dispatch: false },
  );

  // Listen for guardian data updates (which might include profile images)
  guardianUpdated$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GuardianActions.updateGuardianSuccess), // Adjust action name as needed
        tap((action) => {
          // Clear guardian-related image caches
          if (action.guardian?.pictureUrl) {
            const pic = action.guardian.pictureUrl;
            if (typeof pic === 'string') {
              this.imageCacheService.clearAndRefresh(pic);
            } else {
              // pictureUrl is a SafeUrl — convert/cast to string for the cache API
              this.imageCacheService.clearAndRefresh(pic as unknown as string);
            }
          }
        }),
      ),
    { dispatch: false },
  );

  // Listen for learner/student data updates
  learnerUpdated$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(StudentActions.updateStudentSuccess), // Adjust action name as needed
        tap((action) => {
          // Clear learner-related image caches
          if (action.student?.pictureUrl) {
            const pic = action.student.pictureUrl;
            if (typeof pic === 'string') {
              this.imageCacheService.clearAndRefresh(pic);
            } else {
              // pictureUrl is a SafeUrl — convert/cast to string for the cache API
              this.imageCacheService.clearAndRefresh(pic as unknown as string);
            }
          }
        }),
      ),
    { dispatch: false },
  );
}
